import { Link } from '@tanstack/react-router';
import type { MargensResumoResponse } from '@triade/contracts';
import { formatarContagem, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { BuscaMargens } from './tipos';

export interface CartoesMargensProps {
  resumo: MargensResumoResponse | undefined;
  carregando: boolean;
  erro: string | null;
  onTentar: () => void;
  busca: BuscaMargens;
}

const CARTAO = 'rounded-2xl border border-border bg-card px-[18px] py-4';
const ROTULO = 'text-small text-ink-muted';
const VALOR = 'tabular text-[24px] font-semibold whitespace-nowrap';

/** Quatro cartoes do topo (docs/design/Margens.dc.html). Todos os numeros vem de GET /margens/resumo (agregado no servidor). */
export function CartoesMargens({ resumo, carregando, erro, onTentar, busca }: CartoesMargensProps) {
  if (erro && !resumo) {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-3 rounded-2xl border border-destructive/30 bg-danger-bg px-5 py-3 text-small text-danger-ink">
        <span>Não foi possível carregar o resumo: {erro}</span>
        <button type="button" onClick={onTentar} className="rounded-lg border border-border bg-card px-3 py-1 font-medium text-ink hover:bg-line-soft">
          Tentar de novo
        </button>
      </div>
    );
  }
  const nd = (v: React.ReactNode) => (resumo ? v : <span className="text-ink-disabled">{carregando ? '…' : '—'}</span>);
  const minimo = resumo?.exibicao.minimoLucro;
  const prat = resumo?.praticadoAbaixoDoSugerido;
  return (
    <section aria-label="Resumo das margens" aria-busy={carregando} className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
      <div className={CARTAO} data-testid="cartao-abaixo-meta">
        <div className={ROTULO}>Orçamentos abaixo da meta</div>
        <div className={VALOR}>{nd(resumo ? `${formatarContagem(resumo.abaixoDaMeta)} de ${formatarContagem(resumo.orcamentos)}` : null)}</div>
        <div className="text-caption text-ink-secondary">
          {nd(resumo?.exibicao.lucroMedio ? `lucro médio ${formatarFracaoPercentual(resumo.exibicao.lucroMedio)}` : 'sem lucro no período')}
          {' · '}
          <Link
            to="/vender/orcamentos"
            search={{ abaixo: true, ...(busca.dataDe ? { dataDe: busca.dataDe } : {}), ...(busca.dataAte ? { dataAte: busca.dataAte } : {}), ...(busca.escopo === 'APROVADO' ? { status: 'APROVADO' as const } : {}) }}
            className="font-medium text-primary hover:underline"
          >
            ver orçamentos
          </Link>
        </div>
      </div>
      <div className={CARTAO} data-testid="cartao-receita-abaixo">
        <div className={ROTULO}>Receita abaixo da meta</div>
        <div className={VALOR}>{nd(formatarMoeda(resumo?.exibicao.receitaAbaixoDaMeta))}</div>
        <div className="text-caption text-ink-secondary">{nd(`de ${formatarMoeda(resumo?.exibicao.receita)} no total`)}</div>
      </div>
      <div className={CARTAO} data-testid="cartao-abaixo-minimo">
        <div className={ROTULO}>Abaixo do mínimo{minimo ? ` (${formatarFracaoPercentual(minimo).replace(/,00%$/, '%')})` : ''}</div>
        <div className={cn(VALOR, resumo && (resumo.abaixoDoMinimo === 0 ? 'text-success-ink' : 'text-danger-ink'))}>
          {nd(resumo ? formatarContagem(resumo.abaixoDoMinimo) : null)}
        </div>
      </div>
      <div className={CARTAO} data-testid="mc-media">
        <div className={ROTULO}>MC% média</div>
        <div className={VALOR}>{nd(resumo ? formatarFracaoPercentual(resumo.mc.pct) : null)}</div>
        <div className="text-caption text-ink-secondary">margem de contribuição média, ponderada pela receita</div>
      </div>
      <div className={CARTAO} data-testid="cartao-praticado">
        <div className={ROTULO}>Produtos com praticado abaixo do sugerido</div>
        <div className={VALOR}>{nd(prat ? `${formatarContagem(prat.produtos)} de ${formatarContagem(prat.base)}` : null)}</div>
        <div className="text-caption text-ink-secondary">
          {nd(
            <>
              pela última formação gravada ·{' '}
              <Link to="/precificar/lista" search={{ abaixo: true, ...(busca.tabelaId ? { tabelaId: busca.tabelaId } : {}), ...(busca.tipo ? { tipo: busca.tipo } : {}) }} className="font-medium text-primary hover:underline">
                ver na lista
              </Link>
            </>,
          )}
        </div>
      </div>
    </section>
  );
}

/** Rodape do painel: quantos orcamentos ficaram fora da MC% e quantos foram calculados pelo snapshot. Sem nada a dizer, nao renderiza. */
export function RodapeMc({ mc }: { mc: MargensResumoResponse['mc'] | undefined }) {
  if (!mc || (mc.foraDoCalculo === 0 && mc.deSnapshot === 0)) return null;
  const partes: string[] = [];
  if (mc.foraDoCalculo > 0) {
    const n = mc.foraDoCalculo;
    partes.push(
      `${formatarContagem(n)} ${n === 1 ? 'orçamento ficou' : 'orçamentos ficaram'} de fora da MC%: sem o custo separado (materiais/aquisição × processo) no snapshot.`,
    );
  }
  if (mc.deSnapshot > 0) {
    const m = mc.deSnapshot;
    partes.push(`${formatarContagem(m)} ${m === 1 ? 'orçamento calculado' : 'orçamentos calculados'} a partir do snapshot da versão.`);
  }
  return (
    <p role="status" data-testid="mc-fora" className="m-0 text-small text-ink-muted">
      {partes.join(' ')}
    </p>
  );
}
